import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react';
import { useCookies } from 'react-cookie';
import { DEFAULT_HEADERS, to_API_URL } from '../Services/RestService';

export interface PaymentProviderNotificationInfo {
  id: number;
  receivedAt: number;
  notificationType: string;
  notificationSubType?: string;
  discountType?: string;
  appAccountToken?: string;
  userId: string;
  status?: string;
  productId: string;
  transactionId: string;
  message: string;
  paymentId?: string;
  isResubscription: boolean;
  hasError?: boolean;
}

// Enums from the provided types
const NotificationType = {
  SUBSCRIBED: "SUBSCRIBED",
  DID_CHANGE_RENEWAL_PREF: "DID_CHANGE_RENEWAL_PREF",
  DID_CHANGE_RENEWAL_STATUS: "DID_CHANGE_RENEWAL_STATUS",
  OFFER_REDEEMED: "OFFER_REDEEMED",
  DID_RENEW: "DID_RENEW",
  EXPIRED: "EXPIRED",
  DID_FAIL_TO_RENEW: "DID_FAIL_TO_RENEW",
  GRACE_PERIOD_EXPIRED: "GRACE_PERIOD_EXPIRED",
  PRICE_INCREASE: "PRICE_INCREASE",
  REFUND: "REFUND",
  REFUND_DECLINED: "REFUND_DECLINED",
  CONSUMPTION_REQUEST: "CONSUMPTION_REQUEST",
  RENEWAL_EXTENDED: "RENEWAL_EXTENDED",
  REVOKE: "REVOKE",
  TEST: "TEST",
  RENEWAL_EXTENSION: "RENEWAL_EXTENSION",
  REFUND_REVERSED: "REFUND_REVERSED",
  EXTERNAL_PURCHASE_TOKEN: "EXTERNAL_PURCHASE_TOKEN",
  ONE_TIME_CHARGE: "ONE_TIME_CHARGE"
};

const NotificationSubType = {
  INITIAL_BUY: "INITIAL_BUY",
  RESUBSCRIBE: "RESUBSCRIBE",
  DOWNGRADE: "DOWNGRADE",
  UPGRADE: "UPGRADE",
  AUTO_RENEW_ENABLED: "AUTO_RENEW_ENABLED",
  AUTO_RENEW_DISABLED: "AUTO_RENEW_DISABLED",
  VOLUNTARY: "VOLUNTARY",
  BILLING_RETRY: "BILLING_RETRY",
  PRICE_INCREASE: "PRICE_INCREASE",
  GRACE_PERIOD: "GRACE_PERIOD",
  PENDING: "PENDING",
  ACCEPTED: "ACCEPTED",
  BILLING_RECOVERY: "BILLING_RECOVERY",
  PRODUCT_NOT_FOR_SALE: "PRODUCT_NOT_FOR_SALE",
  SUMMARY: "SUMMARY",
  FAILURE: "FAILURE",
  UNREPORTED: "UNREPORTED"
};

// Sample data generator for demonstration purposes
const generateSampleData = (count = 100) => {
  const errorMessages = [
    "Payment processing failed: Invalid payment method",
    "Subscription renewal failed: Card expired",
    "Transaction declined by payment processor",
    "Customer verification failed",
    "Invalid transaction ID provided",
    "Account suspended due to excessive failed attempts",
    "Gateway timeout during processing",
    "Invalid currency code in request",
    "Maximum retry attempts reached",
    "Payment method rejected: High risk assessment"
  ];

  const infoMessages = [
    "Subscription renewed successfully",
    "Auto-renewal enabled by customer",
    "Plan upgraded from Basic to Premium",
    "Refund processed successfully",
    "Payment method updated",
    "Trial period started",
    "Account verified successfully",
    "Price change notification sent to customer",
    "Plan downgraded from Premium to Basic",
    "Welcome offer redeemed"
  ];

  return Array.from({ length: count }, (_, i) => {
    const notificationType = Object.values(NotificationType)[
      Math.floor(Math.random() * Object.values(NotificationType).length)
    ];

    const notificationSubType = Object.values(NotificationSubType)[
      Math.floor(Math.random() * Object.values(NotificationSubType).length)
    ];

    const randomDaysAgo = Math.floor(Math.random() * 30);
    const randomHoursAgo = Math.floor(Math.random() * 24);
    const randomMinutesAgo = Math.floor(Math.random() * 60);

    const date = new Date();
    date.setDate(date.getDate() - randomDaysAgo);
    date.setHours(date.getHours() - randomHoursAgo);
    date.setMinutes(date.getMinutes() - randomMinutesAgo);

    // Generate appropriate message based on notification type
    let message;
    if (["EXPIRED", "DID_FAIL_TO_RENEW", "REFUND_DECLINED", "REVOKE"].includes(notificationType)) {
      message = errorMessages[Math.floor(Math.random() * errorMessages.length)];
    } else {
      message = infoMessages[Math.floor(Math.random() * infoMessages.length)];
    }

    // For test notifications, add a specific prefix
    if (notificationType === "TEST") {
      message = `[TEST] ${message}`;
    }

    // Add transaction ID for some messages
    if (Math.random() > 0.7) {
      const transactionId = Math.random().toString(36).substring(2, 10).toUpperCase();
      message = `${message} (Transaction ID: ${transactionId})`;
    }

    return {
      id: i + 1,
      receivedAt: date,
      notificationType,
      notificationSubType: Math.random() > 0.2 ? notificationSubType : undefined, // 20% chance of undefined
      message
    };
  });
};

// Format date to display in a readable format
const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

// Component to display a badge with color based on notification type
const NotificationBadge = ({ type }: { type: string }) => {
  if (!type) return null;
  let color = "bg-gray-200 text-gray-800";

  if (type.includes("RENEW") || type === "SUBSCRIBED") {
    color = "bg-green-100 text-green-800";
  } else if (type === "EXPIRED" || type === "REVOKE" || type.includes("FAIL")) {
    color = "bg-red-100 text-red-800";
  } else if (type.includes("REFUND")) {
    color = "bg-yellow-100 text-yellow-800";
  } else if (type === "TEST") {
    color = "bg-blue-100 text-blue-800";
  }

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      {type}
    </span>
  );
};

// Component to display a badge with color based on notification subtype
const SubTypeBadge = ({ subType }: { subType: string }) => {
  if (!subType) return null;
  if (subType == null || subType == "") return null;

  let color = "bg-gray-100 text-gray-600";

  if (["UPGRADE", "RESUBSCRIBE", "INITIAL_BUY", "AUTO_RENEW_ENABLED", "ACCEPTED"].includes(subType)) {
    color = "bg-green-50 text-green-600";
  } else if (["DOWNGRADE", "AUTO_RENEW_DISABLED", "VOLUNTARY", "FAILURE"].includes(subType)) {
    color = "bg-yellow-50 text-yellow-600";
  } else if (["BILLING_RETRY", "PRICE_INCREASE", "GRACE_PERIOD", "PRODUCT_NOT_FOR_SALE"].includes(subType)) {
    color = "bg-red-50 text-red-600";
  }

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      {subType}
    </span>
  );
};

const EntitlementEngineDashboard = () => {
  const [cookies] = useCookies();
  const [notifications, setNotifications] = useState<PaymentProviderNotificationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  // Fetch data (simulated for this example)
  const fetchData = () => {
    setLoading(true);
    fetch(to_API_URL("api/v1/dashboard/engine"), {
      method: "GET",
      headers: {
        ...DEFAULT_HEADERS,
        "Authorization": "Bearer " + cookies.accessToken,
      }
    }).then(async result => {
      if (result.ok) {
        const data: PaymentProviderNotificationInfo[] = await result.json();
        console.log("nofitifications", data);
        setNotifications(data);
      }
    }).catch(err => {
      console.log("Failed call", err);
    })
      .finally(() => {
        setLoading(false);
        setLastRefreshed(new Date());
      })



  };

  // Initial data load
  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh timer
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchData();
  };

  // Filter notifications based on search term and type filter
  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = searchTerm === '' ||
      notification.id?.toString().includes(searchTerm) ||
      notification.notificationType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (notification.notificationSubType && notification.notificationSubType.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (notification.message && notification.message.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesTypeFilter = filterType === '' || notification.notificationType === filterType;

    return matchesSearch && matchesTypeFilter;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredNotifications.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);

  const paginate = (pageNumber: number) => {
    if (pageNumber < 1) pageNumber = 1;
    if (pageNumber > totalPages) pageNumber = totalPages;
    setCurrentPage(pageNumber);
  };

  // Refresh interval options
  const refreshOptions = [
    { value: 10, label: '10 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' },
    { value: 300, label: '5 minutes' },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Payment Provider Notifications</h1>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">
            Last refreshed: {formatDate(lastRefreshed)}
          </span>
          <button
            onClick={handleRefresh}
            className="flex items-center px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            <RefreshCw size={16} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between mb-4 space-y-4 md:space-y-0">
        {/* Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={16} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search notifications..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
          />
        </div>

        <div className="flex flex-wrap gap-4">
          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setCurrentPage(1); // Reset to first page on filter change
            }}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Types</option>
            {Object.values(NotificationType).map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          {/* Refresh Interval */}
          <div className="flex items-center">
            <label htmlFor="refresh-interval" className="mr-2 text-sm text-gray-600">Auto-refresh:</label>
            <select
              id="refresh-interval"
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              className="px-2 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {refreshOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {/* Page Size Selector */}
          <div className="flex items-center">
            <label htmlFor="page-size" className="mr-2 text-sm text-gray-600">Items per page:</label>
            <select
              id="page-size"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing page size
              }}
              className="px-2 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received At</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtype</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                  Loading notifications...
                </td>
              </tr>
            ) : currentItems.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                  No notifications found
                </td>
              </tr>
            ) : (
              currentItems.map((notification) => (
                <tr key={notification.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {notification.id || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(new Date(notification.receivedAt))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <NotificationBadge type={notification.notificationType} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {notification.notificationSubType ? (
                      <SubTypeBadge subType={notification.notificationSubType} />
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {notification.notificationType &&
                      <div className={`${notification.notificationType.includes("FAIL") || notification.notificationType === "EXPIRED" || notification.notificationType === "REVOKE" ? "text-red-600 font-medium" : ""}`}>
                        {notification.message}
                      </div>
                    }
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && filteredNotifications.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredNotifications.length)} of {filteredNotifications.length} notifications
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded ${currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              <ChevronLeft size={16} />
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Show at most 5 page buttons, centered around current page
                let pageToShow;
                if (totalPages <= 5) {
                  pageToShow = i + 1;
                } else if (currentPage <= 3) {
                  pageToShow = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageToShow = totalPages - 4 + i;
                } else {
                  pageToShow = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageToShow}
                    onClick={() => paginate(pageToShow)}
                    className={`w-8 h-8 flex items-center justify-center rounded ${currentPage === pageToShow
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {pageToShow}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`px-3 py-1 rounded ${currentPage === totalPages || totalPages === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntitlementEngineDashboard;